import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { countVisibleTabs } from '@/lib/tabOverflow';
import { TYPE } from '@/lib/typography';
import { MenuPopover } from '@/components/ui/Popover';
import { UnreadBadge } from '@/components/ui/UnreadBadge';

export interface TabItem {
  id: string;
  label: string;
  count?: number;
  badge?: number;
}

export function Tabs({
  items,
  value,
  onChange,
  overflowLabel,
  endSlot,
  ariaLabel,
  className,
}: {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  overflowLabel: (hiddenCount: number) => string;
  endSlot?: ReactNode;
  ariaLabel?: string;
  className?: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const update = () => {
      const kids = Array.from(measure.children) as HTMLElement[];
      setVisibleCount(
        countVisibleTabs({
          itemCount: items.length,
          itemWidths: kids.map((kid) => kid.offsetWidth),
          rowWidth: row.clientWidth,
          hasEndSlot: Boolean(endSlot),
          activeIndex: items.findIndex((item) => item.id === value),
        }),
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [endSlot, items, value]);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }
    event.preventDefault();
    const index = items.findIndex((item) => item.id === value);
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else next = items.length - 1;
    const target = items[next];
    if (target) onChange(target.id);
  };

  return (
    <div className={cn('relative', className)}>
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex h-0 overflow-hidden"
      >
        {items.map((item) => (
          <span key={item.id} className={cn(TYPE.control, 'inline-flex h-11 px-s3')}>
            {item.label}
            {item.count != null ? ` ${item.count}` : ''}
            {item.badge != null && item.badge > 0 ? ` ${item.badge}` : ''}
          </span>
        ))}
      </div>

      <div className="flex items-end gap-s4 border-b border-ds-line">
        <div
          ref={rowRef}
          role="tablist"
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
          className="hidden min-w-0 flex-1 items-end nav:flex"
        >
          {visible.map((item) => (
            <TabButton key={item.id} item={item} selected={item.id === value} onSelect={onChange} />
          ))}
          {overflow.length > 0 ? (
            <MenuPopover
              trigger={
                <button
                  type="button"
                  className={cn(TYPE.control, 'more-tab inline-flex h-11 items-center gap-s1 px-s3 text-ds-muted')}
                >
                  {overflowLabel(overflow.length)}
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </button>
              }
            >
              {overflow.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => onChange(item.id)}
                  className={cn(
                    TYPE.control,
                    'flex h-10 w-full items-center rounded-lg px-s2 text-left',
                    item.id === value ? 'bg-ds-teal-tint text-ds-teal' : 'text-ds-text hover:bg-ds-teal-tint',
                  )}
                >
                  {item.label}
                  <UnreadBadge count={item.badge ?? 0} className="ml-s1" />
                </button>
              ))}
            </MenuPopover>
          ) : null}
        </div>

        <div
          role="tablist"
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
          className="flex min-w-0 flex-1 overflow-x-auto scrollbar-hide [mask-image:linear-gradient(to_right,black_80%,transparent)] nav:hidden"
        >
          {items.map((item) => (
            <TabButton key={item.id} item={item} selected={item.id === value} onSelect={onChange} />
          ))}
        </div>

        {endSlot ? <div className="shrink-0 pb-s1">{endSlot}</div> : null}
      </div>
    </div>
  );
}

function TabButton({
  item,
  selected,
  onSelect,
}: {
  item: TabItem;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      onClick={() => onSelect(item.id)}
      className={cn(
        TYPE.control,
        'relative inline-flex h-11 shrink-0 items-center px-s3',
        selected ? 'font-semibold text-ds-teal' : 'font-medium text-ds-muted hover:text-ds-text',
      )}
    >
      {item.label}
      {item.count != null ? (
        <span className="ml-s1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-ds-neutral-tint px-s1 text-[12px] font-semibold text-ds-muted">
          {item.count}
        </span>
      ) : null}
      <UnreadBadge count={item.badge ?? 0} className="ml-s1" />
      {selected ? (
        <span className="absolute inset-x-s3 -bottom-px h-0.5 rounded-sm bg-ds-teal" />
      ) : null}
    </button>
  );
}
